import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/auth';
import CreateShift from '../components/shift_new';
import { PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/20/solid';
import ShiftTableDailyShow from '../components/shift_table_daily_show';
import ShiftTableWeeklyShow from '../components/shift_table_weekly_show';
import Footer from '../components/footer';
import { getWorkingHoursCompound, getWeeklyWorkingHourCompound } from '../services/api_working_hours';
import Toggle from '../components/toggle';
import { Link } from 'wouter';
import PopoverComponentCustom from '../components/popover_component';
import { getCompanySettings } from '../services/api_settings';

const ShiftManagement = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const { token, profile } = useContext(AuthContext);
  const [showCreateShift, setShowCreateShift] = useState(searchParams.has('type'));
  const [showShiftTable, setShowShiftTable] = useState(false);
  const [workingHours, setWorkingHours] = useState([]);
  const [weeklyWorkingHours, setWeeklyWorkingHours] = useState([]);
  const [shiftType, setShiftType] = useState(null);
  const [showAssignWh, setShowAssignWh] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [settingHiddenBreak, setSettingHiddenBreak] = useState(false);

  const isProfileComplete = profile?.has_complete_profile;

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const settings = await getCompanySettings(token, 'payrolls');
        const allowHidden = Boolean(settings?.allow_hidden_breaks ?? false);
        if (!cancelled) setSettingHiddenBreak(allowHidden);
      } catch (e) {
        console.error('Errore caricamento setting:', e);
        if (!cancelled) setSettingHiddenBreak(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  const fetchWorkingHours = async (selectedOption, unit) => {
    if (abortController) abortController.abort();
    const controller = new AbortController();
    setAbortController(controller);

    setWorkingHours([]);
    setWeeklyWorkingHours([]);

    if (selectedOption === 'GIORNALIERO') {
      setLoadingDaily(true);
      try {
        const result = await getWorkingHoursCompound(token, unit?.id, 'shifts', controller.signal);
        setWorkingHours(result || []);
        setLoadingDaily(false);
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error('Errore turni giornalieri:', error);
          setLoadingDaily(false);
        }
      }
    }

    if (selectedOption === 'SETTIMANALE') {
      setLoadingWeekly(true);
      try {
        const result = await getWeeklyWorkingHourCompound(token, unit?.id, null, 'shifts', controller.signal);
        setWeeklyWorkingHours(result || []);
        setLoadingWeekly(false);
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error('Errore turni settimanali:', error);
          setLoadingWeekly(false);
        }
      }
    }
  };

  const handleUnitChange = (unit, shiftType) => {
    setSelectedUnit(unit);
    setShiftType(shiftType);
    setLoadingWeekly(true);
    setLoadingDaily(true);

    if (unit && shiftType) {
      fetchWorkingHours(shiftType, unit);
    }

    if (!unit && shiftType) {
      fetchWorkingHours(shiftType, null);
    }
  };

  useEffect(() => {
    if (shiftType) {
      fetchWorkingHours(shiftType);
    }
  }, [shiftType]);

  useEffect(() => {
    if (shiftType && selectedUnit) {
      setWorkingHours([]);
      setWeeklyWorkingHours([]);
      fetchWorkingHours(shiftType, selectedUnit);
    }
  }, [selectedUnit, shiftType]);

  const handleToggleChange = (selectedOption) => {
    setWorkingHours([]);
    setWeeklyWorkingHours([]);
    setSelectedUnit(null)
    setShiftType(selectedOption);
  };

  const toggleCreateShift = () => {
    setShowCreateShift(!showCreateShift);
  };

  const toggleShiftTable = () => {
    setShowShiftTable(!showShiftTable);
    setShiftType(null);
  };

  const handleAddDailyShift = (newShift) => {
    setWorkingHours(prevWorkingHours => [...prevWorkingHours, newShift]);    
  };

  const handleUpdateWorkingHours = (updatedData) => {
      setWorkingHours(updatedData);      
  };

  const handleSetAssignWh = () => {
    setShowAssignWh(!showAssignWh);
  };


  const handleUpdateWeekWorkingHoursName = (wwhId, newName) => {
    const updatedWorkingHoursData = [
      ...weeklyWorkingHours.map(weekWorkingHour => {
        if (weekWorkingHour.id === wwhId) {
          return { ...weekWorkingHour, name: newName };
        }
        return weekWorkingHour;
      })
    ];

    setWeeklyWorkingHours(updatedWorkingHoursData);
  };

  const handleConfirmEditTimeSlot = (updatedSlot, weekDay, slotIndex, weekWorkingHourId) => {
    setWeeklyWorkingHours(prev => {
      return prev.map(wwh => {
        if (wwh.id !== weekWorkingHourId) return wwh;

        const updatedSlots = [...wwh.slots_by_weekday];
        const daySlotIndex = updatedSlots.findIndex(s => s.week_day === weekDay);

        if (daySlotIndex !== -1) {
          const existingDaySlot = { ...updatedSlots[daySlotIndex] };
          const timeSlots = [...(existingDaySlot.working_hour?.time_slots || existingDaySlot.time_slots || [])];
          timeSlots[slotIndex] = updatedSlot;

          if (existingDaySlot.working_hour) {
            existingDaySlot.working_hour.time_slots = timeSlots;
          } else {
            existingDaySlot.time_slots = timeSlots;
          }

          updatedSlots[daySlotIndex] = existingDaySlot;
        }

        return {
          ...wwh,
          slots_by_weekday: updatedSlots,
        };
      });
    });
  };



  //aggiornamento fascia oraria lavoro settimana e aggiornamento pause
  const handleUpdateWeekWorkingHours = (updatedTimeSlot) => {
    const updatedWorkingHoursData = weeklyWorkingHours.map((weekWorkingHour) => {
        if (weekWorkingHour.slots_by_weekday) {
            const updatedSlotsByWeekday = weekWorkingHour.slots_by_weekday.map((slotByWeekday) => {
                const updatedTimeSlots = slotByWeekday.time_slots.map((timeSlot) => {
                    if (timeSlot.id === updatedTimeSlot.id) {
                        return updatedTimeSlot;
                    }                  
                    return timeSlot;
                });                
                return { ...slotByWeekday, time_slots: updatedTimeSlots };
            });
            return { ...weekWorkingHour, slots_by_weekday: updatedSlotsByWeekday };
        }
        return { ...weekWorkingHour };
    });
    setWeeklyWorkingHours(updatedWorkingHoursData);
  };

  const handleCreateWeekWorkingHours = (newWeekWorkingHour, weekWorkingHourId) => {
    setWeeklyWorkingHours((prevWorkingHoursData) => {
      const data =  prevWorkingHoursData.map((weekWorkingHour) => {
        if (weekWorkingHour.id === weekWorkingHourId) {
          return {
            ...weekWorkingHour,
            slots_by_weekday: [
              ...(weekWorkingHour.slots_by_weekday || []).filter(wwhs => wwhs.week_day !== newWeekWorkingHour.week_day),
              newWeekWorkingHour
            ]
          };
        }
        return weekWorkingHour;
      });
      return data;
    });
  };

  const handleCreateWeekShift = (newShift, workingHourDetails) => {
    let updatedShift = newShift;

    if (workingHourDetails) {
      const updatedSlotsByWeekday = newShift.slots_by_weekday?.map(slotByWeekday => {
        if (slotByWeekday.working_hour === workingHourDetails.id) {
          return {
            ...slotByWeekday,
            working_hour: workingHourDetails
          };
        }
        return slotByWeekday;
      });

      updatedShift = {
        ...newShift,
        slots_by_weekday: updatedSlotsByWeekday
      };
    }
    setWeeklyWorkingHours(prevWeekWorkingHours => [...prevWeekWorkingHours, updatedShift]);
  };

  // elimina turno giornaliero
  const handleDeleteShift = async (id) => {
    const updatedWorkingHours = workingHours.filter(shift => shift.id !== id);
    setWorkingHours(updatedWorkingHours);
  };

  // elimina time slot in workinghour
  const handleDeleteTimeSlot = async (id) => {   
    const updatedWorkingHours = workingHours.map(wh => {
      if (wh.time_slots.some(slot => slot.id === id)) {
        const updatedTimeSlots = wh.time_slots.filter(slot => slot.id !== id);
        return { ...wh, time_slots: updatedTimeSlots };
      }
      return wh;
    });
    setWorkingHours(updatedWorkingHours);
  };

  const handleDeleteWeekTimeSlot = async (id) => {
    try {
      const updatedWeekWorkingHours = weeklyWorkingHours.map((weekWorkingHour) => {
        if (!weekWorkingHour.slots_by_weekday) return weekWorkingHour;

        const updatedSlotsByWeekday = weekWorkingHour.slots_by_weekday.map((slotByWeekday) => {
          const filteredTimeSlots = slotByWeekday.time_slots.filter(slot => slot.id !== id);
          return {
            ...slotByWeekday,
            time_slots: filteredTimeSlots
          };
        });

        return {
          ...weekWorkingHour,
          slots_by_weekday: updatedSlotsByWeekday
        };
      });

      setWeeklyWorkingHours(updatedWeekWorkingHours);
    } catch (error) {
      console.error('Errore durante l\'eliminazione della fascia oraria', error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Errore durante l\'eliminazione della fascia oraria"
      });
    }
  };


  // elimina turno settimanale
  const handleDeleteWeekShift = async (id) => {
    const updatedWorkingHours = weeklyWorkingHours.filter(shift => shift.id !== id); 
  
    setWeeklyWorkingHours(updatedWorkingHours);    
  }; 

  // elimina il giorno nel turno settimanale
  const handleDeleteWeekDay = async (id) => {
    const updatedWeeklyWorkingHours = weeklyWorkingHours.map(week => {
        const updatedSlotsByWeekday = (week.slots_by_weekday || []).filter(slot => slot.id !== id);
        return { ...week, slots_by_weekday: updatedSlotsByWeekday };
    });
    setWeeklyWorkingHours(updatedWeeklyWorkingHours);
  };
  
  return (
    <div className="flex flex-col min-h-screen">
      <PopoverComponentCustom title="Gestione Turni" />
      {profile?.customer_type === 'company' && !isProfileComplete && profile?.company?.email === profile?.the_real_user_email && (
        <div className="w-full">
          <div className="bg-orange-100 text-orange-600 p-4 mb-6">
            <p className="text-center text-sm font-semibold">
              Il profilo non è completo. <br />
              Compila i campi mancanti{' '}
              <Link
                to="/profilo/fatturazione"
                className="text-indigo-600 hover:text-indigo-800 underline"
              >
                cliccando qui
              </Link>
            </p>
          </div>
        </div>
      )}
      <main className="flex-grow mt-10 pb-8">
        <div className='container mx-auto px-5 border-t-2 border-b-2 m-5'>
          <h2 className='text-md px-4 my-5 flex items-center'>CREA NUOVO TURNO
            <button onClick={toggleCreateShift} className="ml-2 text-indigo-600">
              {showCreateShift ? <MinusCircleIcon className="h-7 w-7" /> : <PlusCircleIcon className="h-7 w-7" />}
            </button>
          </h2>

          {showCreateShift && (
            <CreateShift
              onShiftAdded={handleAddDailyShift}
              onWeeklyShiftAdded={handleCreateWeekShift}
              allowHiddenBreaks={settingHiddenBreak}
            />
          )}
        </div>
        <div className='container mx-auto px-5 border-t-2 border-b-2 m-5'>
          <h2 className='text-md px-4 my-5 flex items-center'>I MIEI TURNI
            <button onClick={toggleShiftTable} className="ml-2 text-indigo-600">
              {showShiftTable ? <MinusCircleIcon className="h-7 w-7" /> : <PlusCircleIcon className="h-7 w-7" />}
            </button>
          </h2>
          {showShiftTable && (
            <div className='mb-4'>
              <div className="flex justify-center">
                <Toggle
                  color='indigo'
                  gradeBgColor="600"
                  optionLeft="GIORNALIERO"
                  optionRight="SETTIMANALE"
                  selectedOption={shiftType}
                  onChange={handleToggleChange} 
                />
              </div>

              {shiftType === "GIORNALIERO" && (
                <ShiftTableDailyShow 
                  workingHours={workingHours}
                  loading={loadingDaily} 
                  onDeleteShift={handleDeleteShift} 
                  onDeleteTimeSlot={handleDeleteTimeSlot}
                  onUpdateWorkingHours={handleUpdateWorkingHours}
                  onUnitChange={handleUnitChange} 
                  allowHiddenBreaks={settingHiddenBreak}
                />
              )}
              {shiftType === "SETTIMANALE" && (
                <ShiftTableWeeklyShow 
                  weeklyWorkingHours={weeklyWorkingHours}
                  loading={loadingWeekly} 
                  onDeleteWeekShift={handleDeleteWeekShift}
                  onCreateWeekWorkingHour={handleCreateWeekWorkingHours}
                  onDeleteWeekDay={handleDeleteWeekDay}
                  onCreateWeekShift={handleCreateWeekShift}
                  onUpdatedTimeSlot={handleUpdateWeekWorkingHours}
                  onUpdateWeekWorkingHours={handleUpdateWeekWorkingHoursName}
                  onUnitChange={handleUnitChange}
                  onDeleteWeekTimeSlot={handleDeleteWeekTimeSlot}
                  onEditTimeSlot={handleConfirmEditTimeSlot} 
                  allowHiddenBreaks={settingHiddenBreak} 
                />
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShiftManagement;
